/**
 * Wall extraction for the 3D floor plan viewer.
 *
 * A floor plan sheet draws walls as thick dark strokes and everything else
 * (text, dimension lines, door swings, window glazing, furniture) as thin
 * lines. Thresholding the image and applying a morphological *opening* keeps
 * only strokes thicker than the kernel, which is exactly the wall set:
 * windows and the balcony become gaps, so sunlight enters where it should.
 *
 * Everything here is a pure function over packed `Uint8Array` masks so the
 * algorithm is testable without a canvas; the image loader / rasteriser are
 * the only browser-bound helpers.
 */

export interface Mask { width: number; height: number; data: Uint8Array }

export interface WallSpan { x: number; y: number; length: number }

export interface CropRect { x: number; y: number; w: number; h: number }

export interface WallExtractionOptions {
  /** Luminance below which a pixel counts as ink (0–255); null = DEFAULT_WALL_THRESHOLD. */
  threshold?: number | null;
  /** Opening passes: strokes thinner than ~2·passes px are dropped; null = pick from the stroke thickness. */
  openPasses?: number | null;
  /** Closing passes after opening, to re-join wall corners. */
  closePasses?: number;
  /** Connected components smaller than this (px) are dropped; scaled with resolution when omitted. */
  minArea?: number;
}

export const DEFAULT_WALL_OPTIONS = { threshold: null as number | null, openPasses: null as number | null, closePasses: 1, minArea: 40 };
/** Analysis resolution (longest side) for the whole sheet (crop suggestion) and for the cropped unit (walls). */
export const SHEET_ANALYSIS_SIDE = 1024;
export const WALL_ANALYSIS_SIDE = 1200;

/**
 * Default ink threshold. Brochure sheets draw walls either as black lines or
 * as mid-grey fills (luminance 150–190), so anything that is not near-white
 * paper counts as ink; the thickness filter afterwards decides what is wall.
 * Admins can lower it for sheets with light shading that gets mistaken for
 * walls, or raise it for very faint scans.
 */
export const DEFAULT_WALL_THRESHOLD = 200;
export const WALL_THRESHOLD_RANGE: [number, number] = [120, 235];

export function luminanceMask(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, threshold = DEFAULT_WALL_THRESHOLD): Mask {
  const data = new Uint8Array(width * height);
  for (let i = 0, p = 0; i < data.length; i++, p += 4) {
    const alpha = rgba[p + 3] / 255;
    // Transparent pixels read as paper (white), not ink.
    const lum = (0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2]) * alpha + 255 * (1 - alpha);
    data[i] = lum < threshold ? 1 : 0;
  }
  return { width, height, data };
}

function morph(mask: Mask, keepWhenAll: boolean): Mask {
  const { width, height, data } = mask;
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let all = 1, any = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          const v = yy < 0 || yy >= height || xx < 0 || xx >= width ? 0 : data[yy * width + xx];
          if (!v) all = 0; else any = 1;
        }
      }
      out[y * width + x] = keepWhenAll ? all : any;
    }
  }
  return { width, height, data: out };
}

export const erode = (mask: Mask, passes = 1): Mask => { let m = mask; for (let i = 0; i < passes; i++) m = morph(m, true); return m; };
export const dilate = (mask: Mask, passes = 1): Mask => { let m = mask; for (let i = 0; i < passes; i++) m = morph(m, false); return m; };
/** Opening: erode then dilate. Removes strokes thinner than ~2·passes px, keeps thick ones at their size. */
export const open = (mask: Mask, passes = 1): Mask => dilate(erode(mask, passes), passes);
/** Closing: dilate then erode. Bridges gaps narrower than ~2·passes px. */
export const close = (mask: Mask, passes = 1): Mask => erode(dilate(mask, passes), passes);

export interface Component { area: number; x0: number; y0: number; x1: number; y1: number }

/** 4-connected component labelling. Labels are 1-based; 0 = background. */
export function connectedComponents(mask: Mask): { labels: Int32Array; components: Component[] } {
  const { width, height, data } = mask;
  const labels = new Int32Array(width * height);
  const components: Component[] = [];
  const stack: number[] = [];
  for (let start = 0; start < data.length; start++) {
    if (!data[start] || labels[start]) continue;
    const label = components.length + 1;
    const component: Component = { area: 0, x0: width, y0: height, x1: -1, y1: -1 };
    labels[start] = label; stack.push(start);
    while (stack.length) {
      const index = stack.pop()!;
      const x = index % width, y = (index - x) / width;
      component.area += 1;
      if (x < component.x0) component.x0 = x; if (x > component.x1) component.x1 = x;
      if (y < component.y0) component.y0 = y; if (y > component.y1) component.y1 = y;
      const neighbours = [x > 0 ? index - 1 : -1, x < width - 1 ? index + 1 : -1, y > 0 ? index - width : -1, y < height - 1 ? index + width : -1];
      for (const n of neighbours) if (n >= 0 && data[n] && !labels[n]) { labels[n] = label; stack.push(n); }
    }
    components.push(component);
  }
  return { labels, components };
}

export function removeSmallComponents(mask: Mask, minArea = DEFAULT_WALL_OPTIONS.minArea): Mask {
  const { labels, components } = connectedComponents(mask);
  const data = new Uint8Array(mask.data.length);
  for (let i = 0; i < data.length; i++) {
    const label = labels[i];
    if (label && components[label - 1].area >= minArea) data[i] = 1;
  }
  return { width: mask.width, height: mask.height, data };
}

/** Row run-lengths of set pixels: one box per span in the 3D scene. */
export function wallSpans(mask: Mask): WallSpan[] {
  const spans: WallSpan[] = [];
  const { width, height, data } = mask;
  for (let y = 0; y < height; y++) {
    let x = 0;
    while (x < width) {
      if (!data[y * width + x]) { x++; continue; }
      const start = x;
      while (x < width && data[y * width + x]) x++;
      spans.push({ x: start, y, length: x - start });
    }
  }
  return spans;
}

export interface WallRect { x: number; y: number; width: number; height: number }

/**
 * Greedy rectangle decomposition: spans with the same x / length on
 * consecutive rows merge into one rectangle, so straight walls become a
 * single box instead of one box per pixel row.
 */
export function wallRects(mask: Mask): WallRect[] {
  const rects: WallRect[] = [];
  let openRects = new Map<string, WallRect>();
  const { width, height, data } = mask;
  for (let y = 0; y < height; y++) {
    const next = new Map<string, WallRect>();
    let x = 0;
    while (x < width) {
      if (!data[y * width + x]) { x++; continue; }
      const start = x;
      while (x < width && data[y * width + x]) x++;
      const key = `${start}:${x - start}`;
      const above = openRects.get(key);
      if (above && above.y + above.height === y) { above.height += 1; next.set(key, above); }
      else { const rect = { x: start, y, width: x - start, height: 1 }; rects.push(rect); next.set(key, rect); }
    }
    openRects = next;
  }
  return rects;
}

export const maskNot = (mask: Mask): Mask => ({ width: mask.width, height: mask.height, data: mask.data.map(v => (v ? 0 : 1)) });
export const maskAnd = (a: Mask, b: Mask): Mask => ({ width: a.width, height: a.height, data: a.data.map((v, i) => (v && b.data[i] ? 1 : 0)) });
export const maskAndNot = (a: Mask, b: Mask): Mask => ({ width: a.width, height: a.height, data: a.data.map((v, i) => (v && !b.data[i] ? 1 : 0)) });
export const maskArea = (mask: Mask): number => { let n = 0; for (let i = 0; i < mask.data.length; i++) n += mask.data[i]; return n; };

/** Pixels reachable from the mask border through unset pixels (the "outside"). */
export function floodOutside(mask: Mask): Mask {
  const { width, height, data } = mask;
  const outside = new Uint8Array(width * height);
  const stack: number[] = [];
  const seed = (i: number) => { if (!data[i] && !outside[i]) { outside[i] = 1; stack.push(i); } };
  for (let x = 0; x < width; x++) { seed(x); seed((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { seed(y * width); seed(y * width + width - 1); }
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % width;
    if (x > 0) seed(i - 1);
    if (x < width - 1) seed(i + 1);
    if (i >= width) seed(i - width);
    if (i + width < data.length) seed(i + width);
  }
  return { width, height, data: outside };
}

/**
 * The unit's footprint: everything the *raw* ink encloses. Thin strokes
 * (railings, glazing, door swings) still count here, so balconies and window
 * openings stay inside; `sealPasses` bridges small gaps in that outline.
 * Only components at least `minFraction` of the largest are kept, which
 * drops text glyph interiors and legend boxes. Returns null when the outline
 * leaks (nothing enclosed).
 */
export function footprintMask(ink: Mask, sealPasses = 2, minFraction = 0.05): Mask | null {
  const sealed = close(ink, sealPasses);
  const enclosed = maskNot(floodOutside(sealed));
  const { labels, components } = connectedComponents(enclosed);
  if (!components.length) return null;
  const largest = Math.max(...components.map(c => c.area));
  const data = new Uint8Array(enclosed.data.length);
  for (let i = 0; i < data.length; i++) {
    const label = labels[i];
    if (label && components[label - 1].area >= largest * minFraction) data[i] = 1;
  }
  const footprint = { width: ink.width, height: ink.height, data };
  // Sanity: a real unit fills a good share of the crop and encloses far more
  // floor than ink; a leaked outline leaves only the strokes themselves.
  const area = maskArea(footprint);
  return area > ink.width * ink.height * 0.12 && area > maskArea(ink) * 1.5 ? footprint : null;
}

/** A solid rectangle covering every set pixel (fallback footprint). */
export function boundingRectMask(mask: Mask, pad = 0): Mask | null {
  let x0 = mask.width, y0 = mask.height, x1 = -1, y1 = -1;
  for (let y = 0; y < mask.height; y++) for (let x = 0; x < mask.width; x++) {
    if (!mask.data[y * mask.width + x]) continue;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return null;
  const data = new Uint8Array(mask.width * mask.height);
  for (let y = Math.max(0, y0 - pad); y <= Math.min(mask.height - 1, y1 + pad); y++) {
    for (let x = Math.max(0, x0 - pad); x <= Math.min(mask.width - 1, x1 + pad); x++) data[y * mask.width + x] = 1;
  }
  return { width: mask.width, height: mask.height, data };
}

/**
 * Where the footprint edge is not a wall: windows, balcony fronts, entrance
 * doors. A band `thickness` px wide just inside the footprint edge, minus the
 * (slightly grown) walls. Rendered as glass panes.
 */
export function openingsMask(footprint: Mask, walls: Mask, thickness = 3): Mask {
  const band = maskAndNot(footprint, erode(footprint, thickness));
  return maskAndNot(band, dilate(walls, 1));
}

export interface RoomRegion { id: number; area: number; x0: number; y0: number; x1: number; y1: number; cx: number; cy: number }

/**
 * Enclosed regions between walls inside the footprint, largest first. Used
 * for per-room finishes and tagging; tiny slivers are dropped.
 */
export function roomRegions(footprint: Mask, walls: Mask, minArea: number): { labels: Int32Array; rooms: RoomRegion[] } {
  const floor = maskAndNot(footprint, walls);
  const { labels, components } = connectedComponents(floor);
  const rooms = components.map((c, i) => ({ id: i + 1, area: c.area, x0: c.x0, y0: c.y0, x1: c.x1, y1: c.y1, cx: (c.x0 + c.x1) / 2, cy: (c.y0 + c.y1) / 2 }))
    .filter(room => room.area >= minArea).sort((a, b) => b.area - a.area);
  return { labels, rooms };
}

/**
 * Suggested crop: the bounding box of the largest wall component (the unit
 * outline), padded. Title text, legend and key plan are separate, much
 * smaller components and fall outside it. Percent of the mask size.
 */
export function suggestCrop(wallMask: Mask, padPercent = 3): CropRect | null {
  const { components } = connectedComponents(wallMask);
  if (!components.length) return null;
  // Rank by bounding-box area, not pixel count: a long, thin façade line can
  // hold fewer pixels than a dense key plan yet still be the unit outline.
  const largest = components.reduce((best, c) => {
    const area = (c.x1 - c.x0 + 1) * (c.y1 - c.y0 + 1);
    return area > best.area ? { area, c } : best;
  }, { area: -1, c: components[0] }).c;
  // Merge every component that overlaps the outline's box (interior walls,
  // detached wall stubs), so the crop hugs the whole unit.
  let { x0, y0, x1, y1 } = largest;
  for (const c of components) {
    if (c.x1 < x0 || c.x0 > x1 || c.y1 < y0 || c.y0 > y1) continue;
    x0 = Math.min(x0, c.x0); y0 = Math.min(y0, c.y0); x1 = Math.max(x1, c.x1); y1 = Math.max(y1, c.y1);
  }
  const padX = wallMask.width * padPercent / 100, padY = wallMask.height * padPercent / 100;
  const left = Math.max(0, x0 - padX), top = Math.max(0, y0 - padY);
  const right = Math.min(wallMask.width, x1 + 1 + padX), bottom = Math.min(wallMask.height, y1 + 1 + padY);
  return {
    x: left / wallMask.width * 100, y: top / wallMask.height * 100,
    w: (right - left) / wallMask.width * 100, h: (bottom - top) / wallMask.height * 100,
  };
}

/** Sub-rectangle of a mask, by percent rect of the mask. */
export function cropMask(mask: Mask, crop: CropRect): Mask {
  const x0 = Math.round(crop.x / 100 * mask.width), y0 = Math.round(crop.y / 100 * mask.height);
  const width = Math.max(1, Math.round(crop.w / 100 * mask.width)), height = Math.max(1, Math.round(crop.h / 100 * mask.height));
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const sx = x0 + x, sy = y0 + y;
    data[y * width + x] = sx < mask.width && sy < mask.height ? mask.data[sy * mask.width + sx] : 0;
  }
  return { width, height, data };
}

/** Horizontal flip, for stacks that are mirror images of the drawn plan. */
export function mirrorMask(mask: Mask): Mask {
  const { width, height, data } = mask;
  const out = new Uint8Array(data.length);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) out[y * width + (width - 1 - x)] = data[y * width + x];
  return { width, height, data: out };
}

const count = (mask: Mask) => { let n = 0; for (let i = 0; i < mask.data.length; i++) n += mask.data[i]; return n; };

/**
 * Picks how many opening passes separate walls from everything else, from the
 * ink itself: walls are the thickest strokes, so the erosion depth at which
 * 10% of the ink still survives (`k90`) measures their half-thickness. Half of
 * that removes text and glazing while keeping even thin walls. Never returns
 * more than 6 (a 13 px kernel), never fewer than 1.
 */
export function autoOpenPasses(ink: Mask, maxLevels = 8): number {
  const total = count(ink);
  if (!total) return 1;
  let eroded = ink, k90 = 0;
  for (let k = 1; k <= maxLevels; k++) {
    eroded = erode(eroded, 1);
    if (count(eroded) < total * 0.1) break;
    k90 = k;
  }
  return Math.min(6, Math.max(1, Math.round(k90 / 2)));
}

export function extractWallMask(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, options: WallExtractionOptions = {}): Mask {
  const o = { ...DEFAULT_WALL_OPTIONS, ...options };
  const ink = luminanceMask(rgba, width, height, o.threshold ?? DEFAULT_WALL_THRESHOLD);
  let passes = o.openPasses ?? autoOpenPasses(ink);
  let mask = open(ink, passes);
  // A sheet whose walls are barely thicker than its text can lose everything
  // to the first guess; relax until a meaningful share of the ink survives.
  while (passes > 1 && count(mask) < count(ink) * 0.03) { passes -= 1; mask = open(ink, passes); }
  if (o.closePasses > 0) mask = close(mask, o.closePasses);
  const scale = Math.max(width, height) / 512;
  return removeSmallComponents(mask, options.minArea ?? Math.round(DEFAULT_WALL_OPTIONS.minArea * scale * scale));
}

export interface FloorPlanPixels { rgba: Uint8ClampedArray; width: number; height: number; image: HTMLImageElement }

/** Loads an image once with CORS enabled so its pixels can be read back. */
export function loadFloorPlanImage(imageUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The floor plan image could not be loaded. Check image access / CORS.'));
    image.src = imageUrl;
  });
}

/**
 * RGBA of an image region (percent crop; whole image by default) rasterised so
 * its longest side is `maxSide` (upscaled if the region is small — walls need
 * pixels to be thicker than text at analysis resolution).
 */
export function rasterizeFloorPlan(image: HTMLImageElement, maxSide: number, crop?: CropRect | null): FloorPlanPixels {
  const region = crop && crop.w > 0 && crop.h > 0 ? crop : { x: 0, y: 0, w: 100, h: 100 };
  const sx = image.naturalWidth * region.x / 100, sy = image.naturalHeight * region.y / 100;
  const sw = Math.max(1, image.naturalWidth * region.w / 100), sh = Math.max(1, image.naturalHeight * region.h / 100);
  const scale = maxSide / Math.max(sw, sh);
  const width = Math.max(1, Math.round(sw * scale)), height = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return { rgba: ctx.getImageData(0, 0, width, height).data, width, height, image };
}

/** Downsampled RGBA of an image (longest side = maxSide). Needs CORS-readable images. */
export async function loadFloorPlanPixels(imageUrl: string, maxSide = SHEET_ANALYSIS_SIDE): Promise<FloorPlanPixels> {
  return rasterizeFloorPlan(await loadFloorPlanImage(imageUrl), maxSide);
}
